import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calculator, ChevronDown, ChevronUp, Building2, Activity, Lightbulb, Info } from 'lucide-react';
import LiveCalculationExample from "../../ui/LiveCalculationExample";
import { generateProfessionalSkillExample, generateActivitySkillExample, generateCompanySkillExample } from '@/lib/skills';
import { activities as activitiesData } from '../../../../../db';

export function SkillCalculationExplanation() {
  const [showCalculationEngine, setShowCalculationEngine] = useState(false);

  // Generate sample calculation examples for "Data Engineering" skill
  const sampleSkillsByCategory = {
    profession: [{ name: 'Data Engineering', weight: 0.90 }, { name: 'React', weight: 0.80 }],
    lead: [{ name: 'Team Leadership', weight: 0.75 }],
    soft: [{ name: 'Communication', weight: 0.90 }],
    language: [{ name: 'German', weight: 0.95 }, { name: 'English', weight: 0.90 }],
    tools: [{ name: 'Git', weight: 0.95 }, { name: 'Docker', weight: 0.70 }]
  };

  // Get Mercedes-Benz activities for company calculation
  const mercedesActivities = activitiesData.filter(activity => activity.companyId === 1); // Mercedes-Benz
  
  const professionalExample = generateProfessionalSkillExample(sampleSkillsByCategory);
  const activityExample = generateActivitySkillExample(sampleSkillsByCategory, 7); // Activity 7: IT System- & Process-Consultant
  const companyExample = generateCompanySkillExample(sampleSkillsByCategory, mercedesActivities); // Mercedes activities

  return (
    <Card className="overflow-hidden bg-gray-900 border-gray-700 text-gray-100 animate-gradient">
      <div className="p-4 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Calculator className="w-6 h-6 text-blue-400" />
            <div>
              <h3 className="text-xl font-semibold text-gray-100">Skill Calculation Engine</h3>
              <p className="text-sm text-gray-400 mt-1">
                Dynamic skill level calculation based on project experience, duration, and proficiency weights
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCalculationEngine(!showCalculationEngine)}
            className="flex items-center space-x-1 bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-gray-100"
          >
            {showCalculationEngine ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            <span>{showCalculationEngine ? 'Hide' : 'Show'}</span>
          </Button>
        </div>
      </div>

      {showCalculationEngine && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
        >
          <CardContent className="pt-6 space-y-8 bg-gray-900">
            
            {/* Overview Section */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-2 border-green-700 bg-gray-800">
                <CardHeader className="pb-3">
                  <div className="flex items-center space-x-2">
                    <Lightbulb className="w-5 h-5 text-green-400" />
                    <CardTitle className="text-lg text-green-300">How It Works</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <p className="text-gray-300">Every skill percentage you see is calculated in real-time based on:</p>
                  <ul className="space-y-2 list-disc list-inside text-gray-400">
                    <li><strong className="text-gray-300">Project Duration:</strong> How long you worked with each technology</li>
                    <li><strong className="text-gray-300">Skill Weight:</strong> Your self-assessed proficiency level (0-100%)</li>
                    <li><strong className="text-gray-300">Context Scope:</strong> Professional, company, or activity-specific view</li>
                    <li><strong className="text-gray-300">Multi-Activity Handling:</strong> Proper attribution for cross-company projects</li>
                  </ul>
                  <div className="mt-4 p-3 bg-blue-900/30 border border-blue-700 rounded-lg">
                    <p className="text-xs text-blue-300">
                      💡 <strong>Interactive:</strong> Click any skill percentage throughout the portfolio to see its detailed calculation breakdown!
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-purple-700 bg-gray-800">
                <CardHeader className="pb-3">
                  <div className="flex items-center space-x-2">
                    <Info className="w-5 h-5 text-purple-400" />
                    <CardTitle className="text-lg text-purple-300">Calculation Types</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  <div className="space-y-3">
                    <div className="border-l-4 border-blue-400 pl-3">
                      <h4 className="font-semibold text-blue-300">Professional View</h4>
                      <p className="text-xs text-gray-400">All career projects aggregated with multi-activity attribution</p>
                    </div>
                    <div className="border-l-4 border-green-400 pl-3">
                      <h4 className="font-semibold text-green-300">Company View</h4>
                      <p className="text-xs text-gray-400">Projects filtered to specific company activities</p>
                    </div>
                    <div className="border-l-4 border-amber-400 pl-3">
                      <h4 className="font-semibold text-amber-300">Activity View</h4>
                      <p className="text-xs text-gray-400">Time-bounded skills with project intersection calculations</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Core Formula Section */}
            <Card className="bg-gray-800 border-2 border-gray-600">
              <CardHeader>
                <CardTitle className="text-center text-lg text-gray-100">Core Calculation Formula</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-700 border border-gray-500 p-4 rounded-lg font-mono text-center">
                  <div className="flex items-center justify-center gap-3 text-lg">
                    <span className="text-purple-400">Skill Level</span> 
                    <span className="text-gray-300">=</span>
                    <div className="flex flex-col items-center">
                      <span className="text-blue-400 border-b border-gray-400 pb-1">
                        Σ (Project Duration × Skill Weight)
                      </span>
                      <span className="text-green-400 pt-1">
                        Total Available Duration
                      </span>
                    </div>
                    <span className="text-gray-300">× 100%</span>
                  </div>
                </div>
                <div className="mt-4 text-xs text-center text-gray-400">
                  Where duration varies by context: career total, company attribution, or activity intersection
                </div>
              </CardContent>
            </Card>

            {/* Live Examples Section */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-center text-gray-100">Live Calculation Examples - Data Engineering</h3>
              <p className="text-sm text-gray-400 text-center max-w-3xl mx-auto">
                These are real calculations using actual portfolio data. Each tab shows how the same "Data Engineering" skill 
                has different percentages depending on the calculation context.
              </p>

              <Tabs defaultValue="professional" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-6 bg-gray-700">
                  <TabsTrigger value="professional" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-blue-300">
                    Professional View
                  </TabsTrigger>
                  <TabsTrigger value="company" className="data-[state=active]:bg-green-600 data-[state=active]:text-white text-green-300">
                    Company View
                  </TabsTrigger>
                  <TabsTrigger value="activity" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white text-amber-300">
                    Activity View
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="professional" className="space-y-4">
                  {professionalExample && (
                    <Card className="border-2 border-blue-700 bg-gray-800">
                      <CardHeader className="bg-blue-900/30">
                        <CardTitle className="flex items-center space-x-2 text-blue-300">
                          <Building2 className="w-5 h-5" />
                          <span>Professional View Calculation</span>
                        </CardTitle>
                        <p className="text-sm text-blue-400">
                          Aggregates all career projects with multi-activity attribution for comprehensive skill assessment
                        </p>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <LiveCalculationExample
                          skillName={professionalExample.skillName}
                          calculationType="professional"
                          totalProjects={professionalExample.totalProjects}
                          projectsWithSkill={professionalExample.projectsWithSkill}
                          totalDuration={professionalExample.totalDuration}
                          projects={professionalExample.projects}
                          totalSkillPoints={professionalExample.totalSkillPoints}
                          skillSpecificPoints={professionalExample.skillSpecificPoints}
                          finalResult={professionalExample.finalResult}
                          allProjectDurations={professionalExample.allProjectDurations}
                        />
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="company" className="space-y-4">
                  {companyExample && (
                    <Card className="border-2 border-green-700 bg-gray-800">
                      <CardHeader className="bg-green-900/30">
                        <CardTitle className="flex items-center space-x-2 text-green-300">
                          <Building2 className="w-5 h-5" />
                          <span>Company View Calculation (Mercedes-Benz)</span>
                        </CardTitle>
                        <p className="text-sm text-green-400">
                          Company-specific calculation with proportional attribution for multi-company projects
                        </p>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <LiveCalculationExample
                          skillName={companyExample.skillName}
                          calculationType="company"
                          totalProjects={companyExample.totalProjects}
                          projectsWithSkill={companyExample.projectsWithSkill}
                          totalDuration={companyExample.totalDuration}
                          projects={companyExample.projects}
                          totalSkillPoints={companyExample.totalSkillPoints}
                          skillSpecificPoints={companyExample.skillSpecificPoints}
                          finalResult={companyExample.finalResult}
                          allProjectDurations={companyExample.allProjectDurations}
                        />
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="activity" className="space-y-4">
                  {activityExample && (
                    <Card className="border-2 border-amber-700 bg-gray-800">
                      <CardHeader className="bg-amber-900/30">
                        <CardTitle className="flex items-center space-x-2 text-amber-300">
                          <Activity className="w-5 h-5" />
                          <span>Activity View Calculation (IT System- & Process-Consultant)</span>
                        </CardTitle>
                        <p className="text-sm text-amber-400">
                          Time-bounded calculation using project-activity intersection for precise skill development tracking
                        </p>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <LiveCalculationExample
                          skillName={activityExample.skillName}
                          calculationType="activity"
                          totalProjects={activityExample.totalProjects}
                          projectsWithSkill={activityExample.projectsWithSkill}
                          totalDuration={activityExample.totalDuration}
                          projects={activityExample.projects}
                          totalSkillPoints={activityExample.totalSkillPoints}
                          skillSpecificPoints={activityExample.skillSpecificPoints}
                          finalResult={activityExample.finalResult}
                          activityPeriod={activityExample.activityPeriod}
                          allProjectDurations={activityExample.allProjectDurations}
                        />
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              </Tabs>

              {/* Key Insights */}
              <Card className="bg-gradient-to-r from-indigo-900/50 to-purple-900/50 border-2 border-indigo-700">
                <CardHeader>
                  <CardTitle className="text-center text-indigo-300">Key Insights</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div className="text-center space-y-2">
                      <div className="w-12 h-12 bg-blue-800 rounded-full flex items-center justify-center mx-auto">
                        <span className="text-blue-300 font-bold text-lg">%</span>
                      </div>
                      <h4 className="font-semibold text-gray-200">Context Matters</h4>
                      <p className="text-xs text-gray-400">
                        The same skill can show different percentages based on calculation scope and time boundaries
                      </p>
                    </div>
                    <div className="text-center space-y-2">
                      <div className="w-12 h-12 bg-green-800 rounded-full flex items-center justify-center mx-auto">
                        <span className="text-green-300 font-bold text-lg">⚡</span>
                      </div>
                      <h4 className="font-semibold text-gray-200">Real-Time</h4>
                      <p className="text-xs text-gray-400">
                        All calculations are performed dynamically using live project data with no static caching
                      </p>
                    </div>
                    <div className="text-center space-y-2">
                      <div className="w-12 h-12 bg-purple-800 rounded-full flex items-center justify-center mx-auto">
                        <span className="text-purple-300 font-bold text-lg">🔍</span>
                      </div>
                      <h4 className="font-semibold text-gray-200">Transparent</h4>
                      <p className="text-xs text-gray-400">
                        Every percentage is clickable to reveal the complete calculation breakdown and methodology
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </motion.div>
      )}
    </Card>
  );
}
